import { getTranslate, onLocaleChange } from "../i18n";
import { ProgressReportModal } from "./ProgressReportModal.js";
import {
  addProgressReportFocusedTime,
  loadProgressReportData,
  setProgressReportName,
} from "./progressReportStorage.js";

export function runWavemakerSimulation(scope, levelData, options = {}) {
  if (!scope) {
    throw new Error("[Wavemaker] Paper scope is required to run the simulation.");
  }

  if (typeof scope.activate === "function") {
    scope.activate();
  }

  const { Point, PointText, Path, Rectangle, Segment, Size, Color } = scope;
  const view = scope.view;
  var data = JSON.stringify(levelData);

  /////// WAVE TEST PROTOTYPE ///////

  var gameData = JSON.parse(data);
  var levelNum;
  var onLevelLabelChange =
    typeof options.onLevelLabelChange === "function" ? options.onLevelLabelChange : function () {};
  var onTimeSpeedLabelChange =
    typeof options.onTimeSpeedLabelChange === "function"
      ? options.onTimeSpeedLabelChange
      : function () {};
  var localeUnsubscribe = null;
  var progressReportModal = null;
  var focusTrackingIntervalId = null;
  var focusedTimeStartMs = null;

  /////GAME DATA
  // Debug toggle for quickly testing the "all levels beaten" flow:
  // when true, levels 2..N are marked complete while level 1 remains unbeaten.
  var debugMarkAllButLevelOneBeaten = false;

  var localData = window.localStorage.getItem("levelsComplete");
  var levelsComplete = localData ? JSON.parse(localData) : [];

  if (debugMarkAllButLevelOneBeaten) {
    levelsComplete = [];
    for (var completedLevel = 2; completedLevel <= gameData.levels.length; completedLevel++) {
      levelsComplete.push(completedLevel);
    }
    window.localStorage.setItem("levelsComplete", JSON.stringify(levelsComplete));
  }

  var lowestLevelNotBeaten = 1;

  for (var i = gameData.levels.length; i > 0; i--) {
    if (!levelsComplete.includes(i)) {
      lowestLevelNotBeaten = i;
    }
  }

  var levelNum = lowestLevelNotBeaten;
  var hasBeatenAllLevels = levelsComplete.length == gameData.levels.length;

  /////LEVEL SELECT MENU (EXTERNAL)
  var levelSelectMenu;
  var currentLevelButton;

  createLevelSelectMenu();
  highlightStarsForCompletedLevels();
  highlightCurrentLevel();
  updateLevelSelectButtonLabel();

  //TIME SPEED
  var timeSpeedOptions = [0.5, 1, 1.5];
  var timeSpeed = 1;
  var timeSpeedSelectMenu;
  var timeSpeedButton;

  createTimeSpeedSelectMenu();
  highlightCurrentTimeSpeed();
  updateTimeSpeedButtonLabel();

  //////TUTORIAL
  var grabText;
  var hasGrabbed = levelsComplete.length > 0;
  var hasBeatenALevel = hasGrabbed;

  var grabHandle;
  var grabHandleTween;

  var size = view.size;
  var width = view.size.width;
  var height = view.size.height;
  var deltaT = 0.017; //set on first 'onFrame' event
  var ropeStartingY = height * 0.65;
  var targetZeroHeight = height * 0.5;
  var offsetWidthRatio = 0.08; // what fraction of the view is the offset X of the rope's leftmost point
  var ropeDragInsetRatio = 0.03;

  var winSound = document.getElementById("winSound");
  var winSound2 = document.getElementById("winSound2");
  var winStar;
  var starInnerRad = 0.15 * width;
  var starOuterRad = 1.7 * starInnerRad;
  var starScale2 = 0.1;

  var mouseControls = {
    isMouseDown: false,
    dragCoeffMouseControl: 0.02,
    mouseY: -1,
    controlPointVy: 0,
  };
  var baseMouseDragCoeff = mouseControls.dragCoeffMouseControl;

  var gameConstants = {
    numPoints: 50,
    radius: view.size.width / 150,
    strokeWidth: view.size.width / 120,
    circleStrokeWidthUnhighlighted: view.size.width / 350,
    circleStrokeWidthHighlighted: view.size.width / 350,
    waveSpeedCoeff: 600,
    dampingCoeff: 0,
  };

  var rope;
  var points = [];
  var balls = [];

  var showBalls = true; //show circles on medium?

  var boundaryConditions = {
    FIXED: 1,
    OPEN: 2,
    NONE: 3,
  };

  var currentBoundaryCondition = boundaryConditions.NONE;

  gameConstants.dampingCoeff = currentBoundaryCondition == boundaryConditions.NONE ? 0.004 : 0.008;
  var baseDampingCoeff = gameConstants.dampingCoeff;
  var baseWaveRatio = 0.37;

  var pastPositions = [];
  var currentPositions = [];
  var futurePositions = [];

  var startingX = offsetWidthRatio * width;
  //var stringLengthX = width - 2*startingX; //TESTING ONLY
  var stringLengthX = width - startingX; //REAL

  var dx = stringLengthX / gameConstants.numPoints;

  ////TARGET PATH////
  var targetShape;
  var isTargetActive = true;
  var targetOpacityInactive = 0.6;
  var targetOpacityActive = 1.0;

  //REFERENCE LINES
  var referenceLines = [];
  var persistentRopeGuideLine = null;
  var showReferenceLines = false;
  var referenceLineCount = 9;
  // Keep historical spacing between adjacent lines.
  var referenceLineSpacingRatio = 0.111969;
  var referenceLineStrokeColor = new Color(0.5, 0.5, 0.5, 0.22);
  var referenceLineStrokeWidth = Math.max(1.5, width / 600);
  var referenceCenterLineStrokeWidth = 2 * referenceLineStrokeWidth;
  var persistentRopeGuideLineStrokeColor = new Color(0, 0, 0, 1);
  var persistentRopeGuideLineStrokeWidth = 0.5 * referenceCenterLineStrokeWidth;

  ////WALLS////
  var walls = [];

  //SCORE
  var score; //percent, from 0 to 1

  //TEXT
  var fontSize = width / 30;
  var instructionFontSizeRatio = 0.9;
  var grabPromptOffsetX = Math.max(12, width * 0.02);
  var instructionTextMaxWidthRatio = 0.75;
  var winText;

  //WIN GRAPHICS
  var winGraphicsShowing = false;
  var winGlow;
  var winGlowInitialStroke = 4 * gameConstants.strokeWidth;
  var winGlowFinalStroke = 9 * gameConstants.strokeWidth;
  var winGlowInitialOpacity = 0.8;
  var winGlowFinalOpacity = 0;

  var timeSinceWinInitiated = -1;
  var winFadeTime = 0.6; //seconds

  //TARGET MORPHING
  var isTargetMorphing = false;
  var targetMorphTime = 0.8;
  var timeSinceTargetMorph = -1; //simple timer
  var morphFromPoints;
  var morphToPoints;
  var morphFromHandles;
  var morphToHandles;

  //TIME SLOW EFFECT?
  var isPaused = false;

  //FLIP HORIZONTAL
  var isFlippedHorizontally = false;
  var flipHorizontalButton = document.getElementById("flipHorizontal");
  if (flipHorizontalButton) {
    flipHorizontalButton.addEventListener("click", flipHorizontal);
  }

  function safeInteger(value) {
    if (typeof value !== "number" || !isFinite(value)) {
      return 0;
    }
    return Math.max(0, Math.floor(value));
  }

  function getLevelsBeatenCount() {
    if (!Array.isArray(levelsComplete)) {
      return 0;
    }
    var seen = new Set();
    for (var i = 0; i < levelsComplete.length; i++) {
      var level = levelsComplete[i];
      if (!Number.isFinite(level)) {
        continue;
      }
      var whole = Math.floor(level);
      if (whole >= 1) {
        seen.add(whole);
      }
    }
    return seen.size;
  }

  function buildProgressReportSnapshot(storage) {
    var reportData = storage ?? loadProgressReportData();
    return {
      playerName: reportData.playerName || "",
      totals: {
        timeSpentSeconds: safeInteger((reportData.timeSpentMs || 0) / 1000),
        levelsBeaten: safeInteger(getLevelsBeatenCount()),
      },
    };
  }

  function refreshProgressReportData() {
    if (!progressReportModal) {
      return;
    }
    var storage = loadProgressReportData();
    var snapshot = buildProgressReportSnapshot(storage);
    progressReportModal.setSnapshot(snapshot);
  }

  function handleProgressReportNameCommit(name) {
    setProgressReportName(name);
    refreshProgressReportData();
  }

  function initializeProgressReportModal() {
    if (progressReportModal && progressReportModal.destroy) {
      progressReportModal.destroy();
    }
    progressReportModal = new ProgressReportModal({
      onNameCommit: handleProgressReportNameCommit,
    });
    refreshProgressReportData();
  }

  function isGameFocused() {
    if (typeof document === "undefined") {
      return true;
    }
    if (document.visibilityState === "hidden") {
      return false;
    }
    if (typeof document.hasFocus === "function" && document.hasFocus()) {
      return true;
    }
    if (typeof window !== "undefined") {
      try {
        if (
          window.parent &&
          window.parent !== window &&
          window.parent.document &&
          typeof window.parent.document.hasFocus === "function"
        ) {
          return window.parent.document.hasFocus();
        }
      } catch {
      }
    }
    return false;
  }

  function persistFocusedTime(continueTracking) {
    if (!isGameFocused()) {
      if (focusedTimeStartMs !== null) {
        var inactiveNow = Date.now();
        var inactiveDelta = Math.max(0, inactiveNow - focusedTimeStartMs);
        if (inactiveDelta > 0) {
          addProgressReportFocusedTime(inactiveDelta);
        }
        focusedTimeStartMs = null;
      }
      return;
    }

    var now = Date.now();
    if (focusedTimeStartMs === null) {
      focusedTimeStartMs = now;
      return;
    }
    var delta = Math.max(0, now - focusedTimeStartMs);
    if (delta > 0) {
      addProgressReportFocusedTime(delta);
    }
    focusedTimeStartMs = continueTracking ? now : null;
  }

  function handleFocusTrackingChange() {
    if (isGameFocused()) {
      if (focusedTimeStartMs === null) {
        focusedTimeStartMs = Date.now();
      }
      return;
    }
    persistFocusedTime(false);
  }

  function startFocusTracking() {
    handleFocusTrackingChange();
    if (typeof window !== "undefined") {
      focusTrackingIntervalId = window.setInterval(function () {
        persistFocusedTime(true);
      }, 5000);
      window.addEventListener("focus", handleFocusTrackingChange);
      window.addEventListener("blur", handleFocusTrackingChange);
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleFocusTrackingChange);
    }
  }

  function stopFocusTracking() {
    persistFocusedTime(false);
    if (typeof window !== "undefined") {
      if (focusTrackingIntervalId !== null) {
        window.clearInterval(focusTrackingIntervalId);
      }
      focusTrackingIntervalId = null;
      window.removeEventListener("focus", handleFocusTrackingChange);
      window.removeEventListener("blur", handleFocusTrackingChange);
    }
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", handleFocusTrackingChange);
    }
    focusedTimeStartMs = null;
  }

  function openProgressReportModal() {
    persistFocusedTime(true);
    refreshProgressReportData();
    progressReportModal?.open?.();
  }

  function positionArrayForLevel(l) {
    var positionArray = [];

    var rawPositionArray = gameData.levels[l - 1].targetCoords;

    for (var i = 0; i < rawPositionArray.length; i++) {
      var targetPoint = new Point(
        startingX + rawPositionArray[i].x * stringLengthX,
        targetZeroHeight + rawPositionArray[i].y * 0.15 * height,
      );
      positionArray.push(targetPoint);
    }

    return positionArray;
  }

  function drawTargetShapeForLevelNum(l) {
    removeItemsByRole("target-shape");
    var positionsArray = positionArrayForLevel(l);
    var handlesArray = gameData.levels[l - 1].targetHandles;

    targetShape = new Path();
    targetShape.closed = true;
    targetShape.fillColor = "#a1def9";
    targetShape.data = targetShape.data || {};
    targetShape.data.role = "target-shape";

    for (var i = 0; i < positionsArray.length; i++) {
      var targetPoint = positionsArray[i];
      var handleIn = new Point(
        handlesArray[i].in.x * stringLengthX,
        handlesArray[i].in.y * stringLengthX,
      );
      var handleOut = new Point(
        handlesArray[i].out.x * stringLengthX,
        handlesArray[i].out.y * stringLengthX,
      );
      targetShape.add(new Segment(targetPoint, handleIn, handleOut));
    }

    positionReferenceGuidesBetweenTargetAndRope();
  }

  function removeTargetShape() {
    if (!targetShape) {
      return;
    }

    targetShape.remove();
    removeItemsByRole("target-shape");
  }

  function createReferenceLines() {
    removeReferenceLines();

    var halfLines = Math.floor(referenceLineCount / 2);
    var spacing = height * referenceLineSpacingRatio;
    var centerY = height * 0.5;

    for (var i = -halfLines; i <= halfLines; i++) {
      var lineY = centerY + i * spacing;
      var line = new Path.Line(new Point(0, lineY), new Point(width, lineY));
      line.strokeColor = referenceLineStrokeColor;
      line.strokeWidth = i === 0 ? referenceCenterLineStrokeWidth : referenceLineStrokeWidth;
      line.data = line.data || {};
      line.data.role = "reference-line";
      referenceLines.push(line);
    }

    positionReferenceGuidesBetweenTargetAndRope();
  }

  function removeReferenceLines() {
    for (var i = referenceLines.length - 1; i >= 0; i--) {
      referenceLines[i].remove();
    }
    referenceLines = [];
    removeItemsByRole("reference-line");
  }

  function removePersistentRopeGuideLine() {
    if (!persistentRopeGuideLine) {
      return;
    }

    persistentRopeGuideLine.remove();
    persistentRopeGuideLine = null;
    removeItemsByRole("persistent-rope-guide-line");
  }

  function ropeLeftEdgeX() {
    if (rope && rope.segments && rope.segments.length > 0) {
      return rope.segments[0].point.x;
    }

    return startingX + 1;
  }

  function currentViewVerticalBounds() {
    if (!view || !view.bounds) {
      return { top: 0, bottom: height };
    }

    return {
      top: view.bounds.top,
      bottom: view.bounds.bottom,
    };
  }

  function ensurePersistentRopeGuideLine() {
    var lineX = ropeLeftEdgeX();
    var viewVerticalBounds = currentViewVerticalBounds();

    if (!persistentRopeGuideLine) {
      persistentRopeGuideLine = new Path.Line(
        new Point(lineX, viewVerticalBounds.top),
        new Point(lineX, viewVerticalBounds.bottom),
      );
      persistentRopeGuideLine.strokeColor = persistentRopeGuideLineStrokeColor;
      persistentRopeGuideLine.strokeWidth = persistentRopeGuideLineStrokeWidth;
      persistentRopeGuideLine.data = persistentRopeGuideLine.data || {};
      persistentRopeGuideLine.data.role = "persistent-rope-guide-line";
      return;
    }

    persistentRopeGuideLine.strokeColor = persistentRopeGuideLineStrokeColor;
    persistentRopeGuideLine.strokeWidth = persistentRopeGuideLineStrokeWidth;
    persistentRopeGuideLine.segments[0].point.x = lineX;
    persistentRopeGuideLine.segments[0].point.y = viewVerticalBounds.top;
    persistentRopeGuideLine.segments[1].point.x = lineX;
    persistentRopeGuideLine.segments[1].point.y = viewVerticalBounds.bottom;
  }

  function positionReferenceGuidesBetweenTargetAndRope() {
    var hasReferenceLines = referenceLines.length > 0;
    var hasPersistentLine = Boolean(persistentRopeGuideLine);

    if (!hasReferenceLines && !hasPersistentLine) {
      return;
    }

    var allGuideLines = referenceLines.slice();
    if (persistentRopeGuideLine) {
      allGuideLines.push(persistentRopeGuideLine);
    }

    for (var i = 0; i < allGuideLines.length; i++) {
      var line = allGuideLines[i];
      if (targetShape) {
        line.insertAbove(targetShape);
      }
      if (rope) {
        line.insertBelow(rope);
      }
    }

    // Keep completion overlays above guides even when guides are re-created later.
    if (winStar) {
      winStar.bringToFront();
    }
    if (winText) {
      winText.bringToFront();
    }
  }

  function setShowReferenceLines(enabled) {
    showReferenceLines = Boolean(enabled);
    if (showReferenceLines) {
      createReferenceLines();
    } else {
      removeReferenceLines();
    }
  }

  function setReferenceLineSpacingRatio(nextSpacingRatio) {
    if (!Number.isFinite(nextSpacingRatio) || nextSpacingRatio <= 0) {
      return;
    }

    referenceLineSpacingRatio = nextSpacingRatio;
    if (showReferenceLines) {
      createReferenceLines();
    }
  }

  function createRope() {
    removeItemsByRole("rope");
    removeItemsByRole("rope-ball");
    rope = new Path({
      strokeColor: "black",
      strokeWidth: gameConstants.strokeWidth,
    });
    rope.data = rope.data || {};
    rope.data.role = "rope";

    for (var i = 0; i < gameConstants.numPoints; i++) {
      var pointX = startingX + (stringLengthX * i) / (gameConstants.numPoints - 1);
      //to ensure hit testing for end points
      if (i == 0) {
        pointX += 1;
      } else if (i == gameConstants.numPoints - 1) {
        pointX -= 1;
      }

      var segment = rope.add(new Point(pointX, ropeStartingY));
      var point = segment.point;
      //point.fixed = (i < 0 || i > gameConstants.numPoints - 3);
      point.px = point.x;
      point.py = point.y;

      pastPositions.push(point.y);
      currentPositions.push(point.y);

      var circle = new Path.Circle(new Point(point.x, point.y), gameConstants.radius);
      circle.fillColor = new Color("white");
      circle.strokeColor = "black";
      circle.strokeWidth = gameConstants.circleStrokeWidthUnhighlighted;
      circle.data = circle.data || {};
      circle.data.role = "rope-ball";

      if (!showBalls) circle.visible = false;

      balls[i] = circle;
    }

    ensurePersistentRopeGuideLine();
    positionReferenceGuidesBetweenTargetAndRope();
  }

  function repositionBalls() {
    for (var i = 0; i < balls.length; i++) {
      balls[i].position = rope.segments[i].point;
    }

    if (grabHandle) {
      grabHandle.position = balls[0].position;
    }
  }

  function removeRope() {
    mouseControls.isMouseDown = false;
    mouseControls.mouseY = -1;
    mouseControls.controlPointVy = 0;
    if (rope) {
      rope.remove();
    }
    for (var i = balls.length - 1; i >= 0; i--) {
      balls[i].remove();
    }
    balls = [];
    pastPositions = [];
    currentPositions = [];
    futurePositions = [];
    removeItemsByRole("rope");
    removeItemsByRole("rope-ball");
  }

  function removeItemsByRole(role) {
    if (!scope || !scope.project || !scope.project.activeLayer) {
      return;
    }

    var children = scope.project.activeLayer.children;
    for (var i = children.length - 1; i >= 0; i--) {
      var item = children[i];
      if (item && item.data && item.data.role === role) {
        item.remove();
      }
    }
  }

  //////WALLS//////
  function createWalls() {
    var topLeft = new Point(320, 0);
    var rectSize = new Size(20, 260);
    var rect = new Rectangle(topLeft, rectSize);
    rect.fillColor = "brown";
  }

  function hitTestBalls() {
    var numBallsHit = 0;
    for (var i = 0; i < balls.length; i++) {
      if (targetShape.hitTest(balls[i].position)) {
        balls[i].fillColor = new Color("#ffff76");
        balls[i].strokeWidth = gameConstants.circleStrokeWidthHighlighted;
        numBallsHit++;
      } else {
        balls[i].fillColor = new Color("#000000");
        balls[i].strokeWidth = gameConstants.circleStrokeWidthUnhighlighted;
      }
    }
    if (numBallsHit == balls.length && isTargetActive) {
      isTargetActive = false;
      // updateScoreIndicator(1);
      if (!winGraphicsShowing) initiateWin();
      // bring rope to front so that glow is behind it
      rope.bringToFront();
      for (var i = 0; i < balls.length; i++) {
        balls[i].bringToFront();
      }
      winStar.bringToFront();
      winText.bringToFront();
    }

    score = numBallsHit / gameConstants.numPoints;
  }

  function initiateWin() {
    var playEndWinSound = false;

    if (!levelsComplete.includes(levelNum)) {
      levelsComplete.push(levelNum);
      window.localStorage.setItem("levelsComplete", JSON.stringify(levelsComplete));
      highlightMenuStarForLevelNum(levelNum);
      updateLevelSelectButtonLabel();

      if (levelsComplete.length == gameData.levels.length) {
        hasBeatenAllLevels = true;
        showWinStar();
        showWinText();
        playEndWinSound = true;
        if (document.getElementById("sound-button").checked) {
          winSound2.play();
        }
      }
    }

    if (!playEndWinSound) {
      if (document.getElementById("sound-button").checked) {
        winSound.play();
      }
    }

    winGraphicsShowing = true;
    showWinOutline();

    if (!hasBeatenALevel) {
      hasBeatenALevel = true;
      var grabFadeOut = grabText.tween({ opacity: 0 }, { duration: 500 });
      grabFadeOut.then(function () {
        grabText.visible = false;
      });
    }
  }

  function showWinOutline() {
    //yellow glow
    winGlow = new Path({
      strokeColor: "#FFFF99",
      strokeWidth: winGlowInitialStroke,
      strokeCap: "round",
    });
    for (var i = 0; i < gameConstants.numPoints; i++) {
      var segment = winGlow.add(
        new Point(
          startingX + (stringLengthX * i) / (gameConstants.numPoints - 1),
          currentPositions[i],
        ),
      );
      var point = segment.point;
    }
    winGlow.smooth({ type: "continuous" });

    targetShape.opacity = targetOpacityInactive;
    timeSinceWinInitiated = 0;
  }

  function updateWinGraphics(deltaTime) {
    if (!winGraphicsShowing) return;

    winGlow.strokeWidth =
      (winGlowFinalStroke - winGlowInitialStroke) *
        Math.sin((timeSinceWinInitiated * 2 * Math.PI) / (4 * winFadeTime)) +
      winGlowInitialStroke;

    winGlow.opacity =
      (winGlowFinalOpacity - winGlowInitialOpacity) *
        Math.sin((timeSinceWinInitiated * 2 * Math.PI) / (4 * winFadeTime)) +
      winGlowInitialOpacity;

    timeSinceWinInitiated += deltaTime;

    if (timeSinceWinInitiated >= winFadeTime) {
      winGlow.remove();
      winGraphicsShowing = false;
      timeSinceWinInitiated = -1;

      advanceLevel();
    }
  }

  /////LEVEL FUNCTIONS///////
  function advanceLevel() {
    var nextLevelNum = levelNum < gameData.levels.length ? levelNum + 1 : 1;
    beginMorphingTargetToLevelNum(nextLevelNum);
    unHighlightPreviousLevel();
    levelNum = nextLevelNum;
    highlightCurrentLevel();
    updateLevelSelectButtonLabel();
  }

  function activateLevel() {
    isTargetActive = true;
  }

  /////TARGET SHAPE MORPHING//////
  function beginMorphingTargetToLevelNum(l) {
    isTargetMorphing = true;
    timeSinceTargetMorph = 0;

    morphFromPoints = positionArrayForLevel(levelNum);
    morphToPoints = positionArrayForLevel(l);
    morphFromHandles = gameData.levels[levelNum - 1].targetHandles;
    morphToHandles = gameData.levels[l - 1].targetHandles;
  }

  function updateTargetMorphing(deltaT) {
    timeSinceTargetMorph += deltaT;

    var percentDone = timeSinceTargetMorph / targetMorphTime;

    if (percentDone > 1) {
      percentDone = 1;
    }

    targetShape.opacity =
      targetOpacityInactive + percentDone * (targetOpacityActive - targetOpacityInactive);

    var morphFactor =
      percentDone < 0.5 ? 2 * percentDone * percentDone : -1 + (4 - 2 * percentDone) * percentDone; //ease in out quadratic

    var newX, newY, newHandleInX, newHandleInY, newHandleOutX, newHandleOutY;

    for (var i = 0; i < morphFromPoints.length; i++) {
      //FIND NEW POINTS
      newX = morphFromPoints[i].x + morphFactor * (morphToPoints[i].x - morphFromPoints[i].x);
      newY = morphFromPoints[i].y + morphFactor * (morphToPoints[i].y - morphFromPoints[i].y);

      targetShape.segments[i].point.x = newX;
      targetShape.segments[i].point.y = newY;

      newHandleInX =
        morphFromHandles[i].in.x +
        morphFactor * (morphToHandles[i].in.x - morphFromHandles[i].in.x);
      newHandleInY =
        morphFromHandles[i].in.y +
        morphFactor * (morphToHandles[i].in.y - morphFromHandles[i].in.y);
      newHandleOutX =
        morphFromHandles[i].out.x +
        morphFactor * (morphToHandles[i].out.x - morphFromHandles[i].out.x);
      newHandleOutY =
        morphFromHandles[i].out.y +
        morphFactor * (morphToHandles[i].out.y - morphFromHandles[i].out.y);

      targetShape.segments[i].handleIn.x = newHandleInX * stringLengthX;
      targetShape.segments[i].handleIn.y = newHandleInY * stringLengthX;
      targetShape.segments[i].handleOut.x = newHandleOutX * stringLengthX;
      targetShape.segments[i].handleOut.y = newHandleOutY * stringLengthX;
    }

    if (percentDone >= 1) {
      isTargetMorphing = false;
      timeSinceTargetMorph = 0;

      //initialize next level
      activateLevel();
    }
  }

  function clampToRange(value, minValue, maxValue) {
    return Math.min(maxValue, Math.max(minValue, value));
  }

  function getRopeDragBounds() {
    var viewVerticalBounds = currentViewVerticalBounds();
    var viewHeight = Math.max(0, viewVerticalBounds.bottom - viewVerticalBounds.top);
    var inset = viewHeight * ropeDragInsetRatio;
    var lowerBound = viewVerticalBounds.top + inset;
    var upperBound = viewVerticalBounds.bottom - inset;

    if (lowerBound >= upperBound) {
      var middleY = 0.5 * (viewVerticalBounds.top + viewVerticalBounds.bottom);
      lowerBound = middleY;
      upperBound = middleY;
    }

    return {
      lowerBound: lowerBound,
      upperBound: upperBound,
    };
  }

  function clampRopeDragY(yValue) {
    var bounds = getRopeDragBounds();
    return clampToRange(yValue, bounds.lowerBound, bounds.upperBound);
  }

  ////////LOOP//////////
  function updateWave() {
    //all other points but the first

    // var r = (gameConstants.waveSpeedCoeff * deltaT) / dx;

    // if (r >= 1) r = 0.99;

    //r = 0.43; // testing for uniform speed across view widths
    var effectiveTimeScale = timeSpeed;
    var r = Math.min(baseWaveRatio * effectiveTimeScale, 0.99);
    var dampingFactor = Math.pow(1 - baseDampingCoeff, effectiveTimeScale);
    var dragBounds = getRopeDragBounds();
    var lowerBound = dragBounds.lowerBound;
    var upperBound = dragBounds.upperBound;

    for (var i = 1, l = rope.segments.length - 1; i < l; i++) {
      //figure out future positions
      var futureY =
        r * r * (currentPositions[i - 1] + currentPositions[i + 1]) +
        (2 * (1 - r * r) * currentPositions[i] - pastPositions[i]); //WAVE EQUATION

      //damping
      futureY = currentPositions[i] + dampingFactor * (futureY - currentPositions[i]);

      //checkForWalls();

      futurePositions[i] = futureY;
    }

    var lastIndex = rope.segments.length - 1;

    switch (currentBoundaryCondition) {
      case boundaryConditions.FIXED:
        futurePositions[lastIndex] = rope.segments[lastIndex].point.y;
        break;

      case boundaryConditions.OPEN:
        futurePositions[lastIndex] =
          2 * r * r * currentPositions[lastIndex - 1] +
          2 * (1 - r * r) * currentPositions[lastIndex] -
          pastPositions[lastIndex];
        break;

      case boundaryConditions.NONE:
        futurePositions[lastIndex] =
          (2 * currentPositions[lastIndex] +
            (r - 1) * pastPositions[lastIndex] +
            2 * r * r * (currentPositions[lastIndex - 1] - currentPositions[lastIndex])) /
          (1 + r);
        break;
    }

    for (var i = 1, l = rope.segments.length; i < l; i++) {
      //update past positions
      pastPositions[i] = currentPositions[i];
      currentPositions[i] = futurePositions[i];

      rope.segments[i].point.y = futurePositions[i];
    }

    //MOUSE CONTROLS
    if (mouseControls.isMouseDown) {
      var clampedMouseY = clampToRange(mouseControls.mouseY, lowerBound, upperBound);
      mouseControls.mouseY = clampedMouseY;
      var touchDistance = clampedMouseY - rope.segments[0].point.y;
      var touchDistanceMagnitude = Math.abs(touchDistance);

      if (touchDistance != 0) {
        var touchDistanceSign = touchDistance / touchDistanceMagnitude;

        //VERY IMPORTANT EQUATION WITH IN LINE CONSTANTS UGHHHHH SHOOT ME//
        var speed =
          12 * (2.6 * Math.pow(touchDistanceMagnitude, 0.8) + 0.9 * touchDistanceMagnitude);

        mouseControls.controlPointVy = 0.01 * touchDistanceSign * speed;
      }
    }

    var dragFactor = Math.pow(1 - baseMouseDragCoeff, effectiveTimeScale);
    mouseControls.controlPointVy *= dragFactor;
    rope.segments[0].point.y += mouseControls.controlPointVy * effectiveTimeScale;

    if (rope.segments[0].point.y < lowerBound) {
      rope.segments[0].point.y = lowerBound - 0.5 * (rope.segments[0].point.y - lowerBound); //"bounce" off, double negative
      mouseControls.controlPointVy *= -0.5;
    } else if (rope.segments[0].point.y > upperBound) {
      rope.segments[0].point.y = upperBound - 0.5 * (rope.segments[0].point.y - upperBound);
      mouseControls.controlPointVy *= -0.5;
    }

    currentPositions[0] = rope.segments[0].point.y;

    rope.smooth({ type: "continuous" });
    repositionBalls();
  }

  function createGrabText() {
    grabText = new PointText(new Point(startingX + grabPromptOffsetX, ropeStartingY - fontSize));
    grabText.justification = "left";
    grabText.fillColor = "black";
    grabText.fontSize = fontSize;
    grabText.fontWeight = 600;
    grabText.fontFamily = ["Montserrat", "Tahoma", "sans-serif"];
    setGrabTextContentForState();

    if (hasBeatenALevel) grabText.visible = false;
  }

  function t(key, vars) {
    return getTranslate().t(key, vars);
  }

  function getGrabPromptText() {
    return t("tutorial.grabRope");
  }

  function getInstructionText() {
    return t("tutorial.advanceInstruction");
  }

  function formatLevelLabel(level) {
    return t("ui.levelWithNumber", { level: level });
  }

  function formatTimeSpeedLabel(speed) {
    return t("ui.timeSpeed", { speed: formatTimeSpeed(speed) });
  }

  function getInstructionFontSize() {
    return fontSize * instructionFontSizeRatio;
  }

  function getGrabTextFontFamily() {
    if (!grabText || !grabText.fontFamily) {
      return "Montserrat, Tahoma, sans-serif";
    }
    if (Array.isArray(grabText.fontFamily)) {
      return grabText.fontFamily.join(", ");
    }
    return String(grabText.fontFamily);
  }

  function splitLongToken(token, maxWidth, measure) {
    var segments = [];
    var current = "";
    var chars = Array.from(token);

    for (var i = 0; i < chars.length; i++) {
      var next = chars[i];
      var candidate = current + next;
      if (current && measure(candidate) > maxWidth) {
        segments.push(current);
        current = next;
      } else {
        current = candidate;
      }
    }

    if (current) {
      segments.push(current);
    }

    return segments;
  }

  function wrapParagraphForWidth(paragraph, maxWidth, measure) {
    var trimmed = paragraph.trim();
    if (!trimmed) {
      return "";
    }

    if (!/\s/.test(trimmed)) {
      return splitLongToken(trimmed, maxWidth, measure).join("\n");
    }

    var words = trimmed.split(/\s+/);
    var lines = [];
    var currentLine = "";

    for (var i = 0; i < words.length; i++) {
      var word = words[i];
      var candidate = currentLine ? currentLine + " " + word : word;
      if (!currentLine || measure(candidate) <= maxWidth) {
        currentLine = candidate;
        continue;
      }

      lines.push(currentLine);
      if (measure(word) <= maxWidth) {
        currentLine = word;
      } else {
        var wordSegments = splitLongToken(word, maxWidth, measure);
        for (var segmentIndex = 0; segmentIndex < wordSegments.length - 1; segmentIndex++) {
          lines.push(wordSegments[segmentIndex]);
        }
        currentLine = wordSegments[wordSegments.length - 1] || "";
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines.join("\n");
  }

  function wrapTextForWidth(text, maxWidth) {
    if (!text || !Number.isFinite(maxWidth) || maxWidth <= 0) {
      return text;
    }

    var canvas = view && view.element ? view.element : null;
    if (!canvas || typeof canvas.getContext !== "function") {
      return text;
    }

    var context = canvas.getContext("2d");
    if (!context) {
      return text;
    }

    var fontWeight = grabText && grabText.fontWeight ? grabText.fontWeight : 600;
    var fontSizePx = grabText && grabText.fontSize ? grabText.fontSize : fontSize;
    context.save();
    context.font = fontWeight + " " + fontSizePx + "px " + getGrabTextFontFamily();

    var measure = function (value) {
      return context.measureText(value).width;
    };

    var wrapped = String(text)
      .split("\n")
      .map(function (paragraph) {
        return wrapParagraphForWidth(paragraph, maxWidth, measure);
      })
      .join("\n");

    context.restore();
    return wrapped;
  }

  function getWrappedInstructionText() {
    return wrapTextForWidth(getInstructionText(), width * instructionTextMaxWidthRatio);
  }

  function getInstructionTextCenterPoint() {
    return new Point(0.5 * width, targetShape.position.y);
  }

  function pointForItemCenter(item, centerPoint) {
    if (!item || !centerPoint) {
      return centerPoint;
    }
    return item.point.add(centerPoint.subtract(item.bounds.center));
  }

  function alignGrabInstructionToCenterPoint() {
    if (!grabText || !hasGrabbed) {
      return;
    }
    grabText.point = pointForItemCenter(grabText, getInstructionTextCenterPoint());
  }

  function setGrabTextContentForState() {
    if (!grabText) {
      return;
    }
    if (hasGrabbed) {
      grabText.justification = "center";
      grabText.fontSize = getInstructionFontSize();
      grabText.content = getWrappedInstructionText();
      return;
    }
    grabText.justification = "left";
    grabText.fontSize = fontSize;
    grabText.content = getGrabPromptText();
  }

  function updatePaperTextForLocale() {
    if (grabText) {
      setGrabTextContentForState();
      alignGrabInstructionToCenterPoint();
    }
    if (winText) {
      winText.content = t("win.wavemaster");
    }
  }

  function updateLevelSelectMenuLabels() {
    if (!levelSelectMenu) {
      return;
    }

    for (var i = 0; i < levelSelectMenu.children.length; i++) {
      var levelButton = levelSelectMenu.children[i];
      var levelLabel = levelButton.querySelector(".level-option-label");
      if (levelLabel) {
        levelLabel.textContent = formatLevelLabel(i + 1);
      }
    }
  }

  function updateTimeSpeedMenuLabels() {
    if (!timeSpeedSelectMenu) {
      return;
    }

    for (var i = 0; i < timeSpeedOptions.length; i++) {
      var speedButton = timeSpeedSelectMenu.children[i];
      if (speedButton) {
        speedButton.textContent = formatTimeSpeed(timeSpeedOptions[i]);
      }
    }
  }

  function handleLocaleChange() {
    updatePaperTextForLocale();
    updateLevelSelectMenuLabels();
    updateLevelSelectButtonLabel();
    updateTimeSpeedMenuLabels();
    updateTimeSpeedButtonLabel();
  }

  function createGrabHandle() {
    grabHandle = new Path.Circle({
      radius: 1.6 * gameConstants.radius,
      applyMatrix: false,
    });
    grabHandle.strokeColor = "black";
    grabHandle.strokeWidth = 0.6 * gameConstants.circleStrokeWidthUnhighlighted;

    growGrabHandle();
  }

  function growGrabHandle() {
    grabHandleTween = grabHandle
      .tweenTo(
        { scaling: 1.1 },
        {
          easing: "easeOutQuad",
          duration: 600,
        },
      )
      .then(shrinkGrabHandle);
  }

  function shrinkGrabHandle() {
    grabHandleTween = grabHandle
      .tweenTo(
        { scaling: 0.6 },
        {
          easing: "easeInQuad",
          duration: 600,
        },
      )
      .then(growGrabHandle);
  }

  function onFrame(event) {
    ensurePersistentRopeGuideLine();

    if (isPaused) {
      return;
    }

    updateWave();
    hitTestBalls();
    if (winGraphicsShowing) {
      updateWinGraphics(event.delta);
    } else if (isTargetMorphing) {
      updateTargetMorphing(event.delta);
    }
  }

  //MOUSE HANDLERS

  function onMouseMove(event) {
    if (isPaused) {
      return;
    }

    //if the mouse is not down
    if (!mouseControls.isMouseDown) return;

    //if the mouse is down
    mouseControls.mouseY = clampRopeDragY(event.point.y);
  }

  function onMouseDown(event) {
    if (isPaused) {
      return;
    }

    handleFocusTrackingChange();
    mouseControls.isMouseDown = true;
    mouseControls.mouseY = clampRopeDragY(event.point.y);

    if (!hasGrabbed) {
      hasGrabbed = true;
      setGrabTextContentForState();
      var instructionCenterPoint = getInstructionTextCenterPoint();
      var instructionTargetPoint = pointForItemCenter(grabText, instructionCenterPoint);
      grabText.tween(
        {
          point: instructionTargetPoint,
        },
        {
          easing: "easeOutQuad",
          duration: 600,
        },
      );
    }

    grabHandleTween.stop();
    grabHandle.scaling = 1;
  }

  function onMouseUp(event) {
    if (isPaused) {
      return;
    }

    mouseControls.isMouseDown = false;
    mouseControls.mouseY = -1;

    grabHandle.visible = true;
    grabHandle.scaling = 1;
    growGrabHandle();
  }

  function createWinStar() {
    winStar = new Path.Star({
      center: new Point(width / 2, 0.4 * height),
      points: 5,
      radius1: starInnerRad,
      radius2: starOuterRad,
      applyMatrix: false,
    });
    winStar.fillColor = "#ffe77d";
    winStar.rotate(180);
    winStar.visible = false;
  }

  function showWinStar() {
    winStar.visible = true;
    winStar.scale(0.1);
    var zoomTween = winStar.tween({ scaling: 0.6 }, { easing: "easeOutQuad", duration: 800 });

    zoomTween.then(function () {
      winStar.tween(
        {
          position: winStarPosition(),
          scaling: starScale2,
          // rotation: 540,
        },
        {
          easing: "easeInCubic",
          duration: 860,
        },
      );
    });
  }

  function preShowWinStar() {
    winStar.visible = true;
    winStar.position = winStarPosition();
    winStar.scaling = starScale2;
  }

  function winStarPosition() {
    var horizontalInset = 1.36 * starOuterRad * starScale2;
    var verticalInset = 1.35 * starOuterRad * starScale2;

    return isFlippedHorizontally
      ? new Point(width - horizontalInset, verticalInset)
      : new Point(horizontalInset, verticalInset);
  }

  function createWinText() {
    winText = new PointText(new Point(width / 2, height * 0.5));
    winText.justification = "center";
    winText.fillColor = "black";
    winText.fontSize = Math.min(width / 10, 60);
    winText.fontWeight = 600;
    winText.fontFamily = ["Montserrat", "Tahoma", "sans-serif"];
    winText.content = t("win.wavemaster");
    winText.visible = false;
  }

  function showWinText() {
    winText.visible = true;
    winText.opacity = 0;
    var fadeInTween = winText.tween({ opacity: 1 }, { easing: "easeOutQuad", duration: 800 });

    fadeInTween.then(function () {
      winText.tween(
        {
          opacity: 0,
        },
        {
          easing: "easeInQuad",
          duration: 860,
        },
      );
    });
  }

  /////LEVEL SELECT MENU FUNCTIONALITY

  function resetDropdownMenu(menu, handler) {
    if (!menu) {
      return null;
    }

    menu.removeEventListener("click", handler);

    while (menu.firstChild) {
      menu.removeChild(menu.firstChild);
    }

    return menu;
  }

  //LEVEL SELECT MENU
  function createLevelSelectMenu() {
    levelSelectMenu = document.getElementById("level-select-menu");

    if (!levelSelectMenu) {
      return;
    }

    resetDropdownMenu(levelSelectMenu, selectLevel);

    for (var i = 1; i <= gameData.levels.length; i++) {
      var levelButton = document.createElement("BUTTON");
      levelButton.classList.add("dropdown-item");
      levelButton.type = "button";

      var starIcon = document.createElement("SPAN");
      starIcon.classList.add("star-icon");
      starIcon.classList.add("star-icon-unhighlighted");
      starIcon.setAttribute("aria-hidden", "true");
      levelButton.appendChild(starIcon);

      var levelLabel = document.createElement("SPAN");
      levelLabel.classList.add("level-option-label");
      levelLabel.textContent = formatLevelLabel(i);
      levelButton.appendChild(levelLabel);

      levelSelectMenu.appendChild(levelButton);
    }

    levelSelectMenu.addEventListener("click", selectLevel);
  }

  function selectLevel(e) {
    var element = e.target;
    var button = element instanceof Element ? element.closest("button.dropdown-item") : null;

    var newLevelNum = button
      ? Array.prototype.indexOf.call(levelSelectMenu.children, button) + 1
      : 0;

    if (newLevelNum == levelNum || newLevelNum == 0) {
      return;
    } else {
      unHighlightPreviousLevel();
      levelNum = newLevelNum;
      highlightCurrentLevel();
      removeTargetShape();
      drawTargetShapeForLevelNum(levelNum);
      removeRope();
      createRope();
      updateLevelSelectButtonLabel();
      grabText.bringToFront();

      if (isTargetMorphing) {
        isTargetMorphing = false;
      }

      if (winGraphicsShowing) {
        winGlow.remove();
        winGraphicsShowing = false;
        timeSinceWinInitiated = -1;
      }
    }
  }

  function unHighlightPreviousLevel() {
    currentLevelButton.classList.remove("dropdown-item-highlighted");
  }

  function highlightCurrentLevel() {
    currentLevelButton = levelSelectMenu.children[levelNum - 1];
    currentLevelButton.classList.add("dropdown-item-highlighted");
  }

  function updateLevelSelectButtonLabel() {
    var levelSelectButton = document.getElementById("dropdownMenu2");
    if (!levelSelectButton) {
      return;
    }

    var labelText = formatLevelLabel(levelNum);
    var levelSelectButtonLabel = levelSelectButton.querySelector(".level-select-button-label");
    if (levelSelectButtonLabel) {
      levelSelectButtonLabel.textContent = labelText;
    } else {
      levelSelectButton.textContent = labelText;
    }
    onLevelLabelChange(labelText);

    var levelSelectButtonStar = levelSelectButton.querySelector(".level-select-button-star");
    if (!levelSelectButtonStar) {
      return;
    }

    levelSelectButtonStar.classList.remove(
      "star-icon-highlighted",
      "star-icon-unhighlighted",
      "star-icon-button-unhighlighted",
    );

    if (levelsComplete.includes(levelNum)) {
      levelSelectButtonStar.classList.add("star-icon-highlighted");
    } else {
      levelSelectButtonStar.classList.add("star-icon-button-unhighlighted");
    }
  }

  function highlightStarsForCompletedLevels() {
    for (var i = 0; i < levelsComplete.length; i++) {
      highlightMenuStarForLevelNum(levelsComplete[i]);
    }
  }

  function highlightMenuStarForLevelNum(levelNum) {
    var curLevelBtn = levelSelectMenu.children[levelNum - 1];
    if (!curLevelBtn) {
      return;
    }

    var starIcon = curLevelBtn.querySelector(".star-icon");
    if (!starIcon) {
      return;
    }

    starIcon.classList.remove("star-icon-unhighlighted");
    starIcon.classList.add("star-icon-highlighted");
  }

  //TIME SPEED MENU
  function createTimeSpeedSelectMenu() {
    timeSpeedSelectMenu = document.getElementById("time-speed-select-menu");

    if (!timeSpeedSelectMenu) {
      return;
    }

    resetDropdownMenu(timeSpeedSelectMenu, selectTimeSpeed);

    for (var i = 0; i < timeSpeedOptions.length; i++) {
      var speedButton = document.createElement("BUTTON");
      speedButton.classList.add("dropdown-item");
      speedButton.type = "button";
      speedButton.textContent = formatTimeSpeed(timeSpeedOptions[i]);
      timeSpeedSelectMenu.appendChild(speedButton);
    }

    timeSpeedSelectMenu.addEventListener("click", selectTimeSpeed);
  }

  function selectTimeSpeed(e) {
    var element = e.target;
    var newTimeSpeedIndex = Array.prototype.indexOf.call(timeSpeedSelectMenu.children, element);

    if (
      newTimeSpeedIndex < 0 ||
      newTimeSpeedIndex >= timeSpeedOptions.length ||
      timeSpeedOptions[newTimeSpeedIndex] === timeSpeed
    ) {
      return;
    }

    setTimeSpeed(timeSpeedOptions[newTimeSpeedIndex]);
  }

  function setTimeSpeed(newTimeSpeed) {
    timeSpeed = newTimeSpeed;
    updateTimeSpeedButtonLabel();
    unHighlightPreviousTimeSpeed();
    highlightCurrentTimeSpeed();
  }

  function updateTimeSpeedButtonLabel() {
    var speedSelectButton = document.getElementById("dropdownMenuSpeed");
    var label = formatTimeSpeedLabel(timeSpeed);
    if (speedSelectButton) {
      speedSelectButton.textContent = label;
    }
    onTimeSpeedLabelChange(label);
  }

  function unHighlightPreviousTimeSpeed() {
    if (!timeSpeedButton) {
      return;
    }

    timeSpeedButton.classList.remove("dropdown-item-highlighted");
  }

  function highlightCurrentTimeSpeed() {
    if (!timeSpeedSelectMenu) {
      return;
    }

    var currentTimeSpeedIndex = timeSpeedOptions.indexOf(timeSpeed);
    if (currentTimeSpeedIndex < 0) {
      return;
    }

    timeSpeedButton = timeSpeedSelectMenu.children[currentTimeSpeedIndex];
    if (!timeSpeedButton) {
      return;
    }
    timeSpeedButton.classList.add("dropdown-item-highlighted");
  }

  function formatTimeSpeed(speed) {
    var locale = getTranslate().lang || "en";
    var fractionDigits = Number.isInteger(speed) ? 0 : 1;
    try {
      return (
        new Intl.NumberFormat(locale, {
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        }).format(speed) + "x"
      );
    } catch {
      return speed + "x";
    }
  }

  //FLIP HORIZONTAL
  function flipHorizontal() {
    isFlippedHorizontally = !isFlippedHorizontally;

    winText.scale(-1, 1);
    grabText.scale(-1, 1);

    if (isFlippedHorizontally) {
      document.getElementById("myCanvas").classList.add("canvas-flipped");
    } else {
      document.getElementById("myCanvas").classList.remove("canvas-flipped");
    }

    winStar.position = winStarPosition();
  }

  function setPaused(paused) {
    isPaused = Boolean(paused);

    if (isPaused) {
      mouseControls.isMouseDown = false;
      mouseControls.mouseY = -1;
      mouseControls.controlPointVy = 0;
    }
  }

  // Wire Paper.js view callbacks so the simulation and mouse controls run.
  view.onFrame = onFrame;
  view.onMouseDown = onMouseDown;
  view.onMouseMove = onMouseMove;
  view.onMouseUp = onMouseUp;

  drawTargetShapeForLevelNum(levelNum);
  createGrabText();
  createGrabHandle();
  createRope();
  createWalls();
  createWinStar();
  if (hasBeatenAllLevels) {
    preShowWinStar();
  }
  createWinText();
  initializeProgressReportModal();
  startFocusTracking();
  handleLocaleChange();
  localeUnsubscribe = onLocaleChange(handleLocaleChange);

  return {
    setPaused: setPaused,
    setShowReferenceLines: setShowReferenceLines,
    setReferenceLineSpacingRatio: setReferenceLineSpacingRatio,
    openProgressReport: openProgressReportModal,
    destroy: function () {
      stopFocusTracking();
      if (flipHorizontalButton) {
        flipHorizontalButton.removeEventListener("click", flipHorizontal);
      }
      if (levelSelectMenu) {
        levelSelectMenu.removeEventListener("click", selectLevel);
      }
      if (timeSpeedSelectMenu) {
        timeSpeedSelectMenu.removeEventListener("click", selectTimeSpeed);
      }
      if (localeUnsubscribe) {
        localeUnsubscribe();
        localeUnsubscribe = null;
      }
      if (progressReportModal && progressReportModal.destroy) {
        progressReportModal.destroy();
      }
      progressReportModal = null;
      removeReferenceLines();
      removePersistentRopeGuideLine();
      view.onFrame = null;
      view.onMouseDown = null;
      view.onMouseMove = null;
      view.onMouseUp = null;
    },
  };
}
